import React, { useCallback, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { SearchOptionsQuery, SearchProductOptionsQuery, SearchGlobalPosOptionsByPosIds } from 'queries/Option';

import { MenuOption, PosOption, Product, SearchGlobalPosOptionsResult, Venue } from 'generated/graphql';
import { formatPrice, posOptionToMMOption, sanitiseUrlParam } from 'utils';
import ItemInlineSearch, { ItemType } from 'components/InlineSearch/ItemInlineSearch';
import { useDebouncedCallback } from 'use-debounce';
import { useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import styles from './AddOptionGroup.module.scss';
import { useAppContext } from '../../../../AppContext';
import { OptionSource } from '../../../../types';

const buttonText = '+ Add option group';
const initText = 'Option group name';

type Props = {
  currentVenue: Venue;
  item: Product;
  handleAdd: (option: MenuOption) => void;
};

const AddOptionGroup: React.FC<Props> = ({ currentVenue, item, handleAdd }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<(PosOption | MenuOption)[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setOffset] = useState(0);

  const { menuId } = useParams();
  const sanitisedMenuId = sanitiseUrlParam(menuId);

  const client = useApolloClient();
  const { draftMenus } = useAppContext();

  const [allAppProducts] = useMemo(() => {
    const products: Product[] = [];
    const options: MenuOption[] = [];

    currentVenue.menus?.forEach((menu) => {
      const draft = draftMenus.find((dm) => dm.mmId === menu.mmId);
      const currentMenu = draft || cloneDeep(menu);
      currentMenu.products.forEach((product) => {
        const productExists = products.find((p) => p.mmId === product.mmId);
        if (!productExists) {
          products.push(product);
        }
        product.options.forEach((option) => {
          const optionExists = options.find((p) => p.mmId === option.mmId);

          if (!optionExists) {
            options.push(option);
          }
        });
      });
    });

    return [
      products.sort((a, b) => a.name.localeCompare(b.name)),
      options.sort((a, b) => a.name.localeCompare(b.name))
    ];
  }, [currentVenue.menus, draftMenus]);

  const searchCurrentVenueForOptions = useCallback(
    async (
      text: string
    ): Promise<{ currentMenuOptionsSearchResult: MenuOption[]; otherMenuOptionsSearchResult: MenuOption[] }> => {
      const formattedText = text.toLowerCase().trim();
      if (!formattedText) {
        return {
          currentMenuOptionsSearchResult: [],
          otherMenuOptionsSearchResult: []
        };
      }
      const uniquePosIds = new Set<string>();

      const localMenuSearchResult = currentVenue?.menus?.reduce(
        (
          result: { currentMenuOptionsSearchResult: MenuOption[]; otherMenuOptionsSearchResult: MenuOption[] },
          currentPublishedMenu
        ) => {
          const draft = draftMenus.find((dm) => dm.mmId === currentPublishedMenu.mmId);
          const currentMenu = draft || cloneDeep(currentPublishedMenu);
          currentMenu.products.forEach((product) => {
            product.options
              .filter(
                (option) =>
                  option.name.toLowerCase().includes(formattedText) ||
                  option.posId.toLowerCase().includes(formattedText)
              )
              .forEach((option) => {
                if (currentMenu.mmId === sanitisedMenuId) {
                  if (!result.currentMenuOptionsSearchResult.some((r) => r.mmId === option.mmId)) {
                    result.currentMenuOptionsSearchResult.push(option);
                    uniquePosIds.add(option.posId);
                  }
                } else if (!result.otherMenuOptionsSearchResult.some((r) => r.mmId === option.mmId)) {
                  result.otherMenuOptionsSearchResult.push(option);
                  uniquePosIds.add(option.posId);
                }
              });
          });
          return result;
        },
        {
          currentMenuOptionsSearchResult: [],
          otherMenuOptionsSearchResult: []
        }
      ) || {
        currentMenuOptionsSearchResult: [],
        otherMenuOptionsSearchResult: []
      };

      const globalOptionsSearchResult = await client.query<{
        searchGlobalPosOptionsByPosIds: SearchGlobalPosOptionsResult;
      }>({
        query: SearchGlobalPosOptionsByPosIds,
        variables: {
          locationId: currentVenue.hashedId,
          posIds: Array.from(uniquePosIds)
        }
      });
      const globalOptions = globalOptionsSearchResult.data.searchGlobalPosOptionsByPosIds.items;
      localMenuSearchResult?.otherMenuOptionsSearchResult.forEach((option) => {
        if (option.clonedPosId || globalOptions.some((o) => o.posId === option.posId)) {
          option.source = OptionSource.APP_GLOBAL;
        } else {
          option.source = OptionSource.POS_PRODUCT;
        }
      });
      localMenuSearchResult?.currentMenuOptionsSearchResult.forEach((option) => {
        if (option.clonedPosId || globalOptions.some((o) => o.posId === option.posId)) {
          option.source = OptionSource.APP_GLOBAL;
        } else {
          option.source = OptionSource.POS_PRODUCT;
        }
      });
      return localMenuSearchResult;
    },
    [client, currentVenue.hashedId, currentVenue?.menus, draftMenus, sanitisedMenuId]
  );

  const handleAddOption = useCallback(
    (index: number) => {
      if (!searchResults[index]) return;

      if (searchResults[index].__typename === 'MenuOption') {
        handleAdd(searchResults[index] as MenuOption);
        return;
      }

      if (searchResults[index].__typename === 'PosOption') {
        handleAdd(
          posOptionToMMOption(
            searchResults[index] as PosOption,
            item.variablesByApp.map(({ app }) => app)
          )
        );
      }
    },
    [handleAdd, item.variablesByApp, searchResults]
  );

  const handleSearch = async (searchTerm: string): Promise<void> => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsLoading(true);

    const [globalOptions, productOptions] = await Promise.all([
      client.query<{ searchGlobalPosOptions: SearchGlobalPosOptionsResult }>({
        query: SearchOptionsQuery,
        variables: {
          locationId: currentVenue.hashedId,
          searchTerm,
          offset: 0
        }
      }),
      client.query<{ searchProductPosOptions: PosOption[] }>({
        query: SearchProductOptionsQuery,
        variables: {
          locationId: currentVenue.hashedId,
          productPosId: item.posId,
          searchTerm
        }
      })
    ]);

    setOffset(globalOptions?.data.searchGlobalPosOptions.limit || 0);
    if (
      !globalOptions.data ||
      globalOptions.data.searchGlobalPosOptions.count <= globalOptions.data.searchGlobalPosOptions.limit
    )
      setHasMore(false);
    else {
      setHasMore(true);
    }

    const globalOptionsSearchResults = globalOptions?.data?.searchGlobalPosOptions.items;
    const posProductOptionsSearchResults = productOptions?.data?.searchProductPosOptions;
    const { currentMenuOptionsSearchResult, otherMenuOptionsSearchResult } = await searchCurrentVenueForOptions(
      searchTerm
    );

    const finalResult = [
      ...currentMenuOptionsSearchResult.filter(
        (o) => o.source === OptionSource.APP_GLOBAL || posProductOptionsSearchResults.some((po) => po.posId === o.posId) // Make sure clones are considered
      ),
      ...otherMenuOptionsSearchResult.filter((o) => {
        return (
          (o.source === OptionSource.APP_GLOBAL &&
            !currentMenuOptionsSearchResult.some((currentOption) =>
              currentOption.clonedPosId ? currentOption.mmId === o.mmId : currentOption.posId === o.posId
            )) ||
          (posProductOptionsSearchResults.some((po) => po.posId === o.posId) &&
            !currentMenuOptionsSearchResult.some((currentOption) => currentOption.posId === o.posId)) // Menu option vs POS Option, Make sure clones are considered
        );
      }),
      ...globalOptionsSearchResults.filter((option) => {
        return (
          !currentMenuOptionsSearchResult.some((currentOption) => currentOption.posId === option.posId) &&
          !otherMenuOptionsSearchResult.some((otherOption) => otherOption.posId === option.posId)
        );
      }),
      ...posProductOptionsSearchResults.filter((option) => {
        return (
          !currentMenuOptionsSearchResult.some((currentOption) => currentOption.posId === option.posId) &&
          !otherMenuOptionsSearchResult.some((otherOption) => otherOption.posId === option.posId) &&
          !globalOptionsSearchResults.some((globalOption) => globalOption.posId === option.posId)
        );
      })
    ];

    setSearchResults(finalResult);

    setIsLoading(false);
  };

  const debouncedSearch = useDebouncedCallback(async (searchTerm) => await handleSearch(searchTerm), 200);

  const searchMorePOSResults = useCallback(
    async (searchTerm: string): Promise<void> => {
      const { data } = await client.query<{ searchGlobalPosOptions: SearchGlobalPosOptionsResult }>({
        query: SearchOptionsQuery,
        variables: {
          locationId: currentVenue.hashedId,
          searchTerm,
          offset: currentOffset
        }
      });
      if (!data?.searchGlobalPosOptions.items) {
        setHasMore(false);
        return;
      }
      const newOffset = currentOffset + data.searchGlobalPosOptions.limit;
      setOffset(newOffset);
      if (data.searchGlobalPosOptions.count <= newOffset) setHasMore(false);
      else {
        setHasMore(true);
      }

      const { currentMenuOptionsSearchResult, otherMenuOptionsSearchResult } = await searchCurrentVenueForOptions(
        searchTerm
      );

      const finalResults = data?.searchGlobalPosOptions.items.filter(
        (result: PosOption) =>
          !currentMenuOptionsSearchResult.some((option) => option.posId === result.posId) &&
          !otherMenuOptionsSearchResult.some((option) => option.posId === result.posId)
      );

      setSearchResults([...searchResults, ...finalResults]);
    },
    [client, currentOffset, currentVenue.hashedId, searchCurrentVenueForOptions, searchResults]
  );

  const optionToItem = useCallback(
    (option: PosOption | MenuOption): ItemType => {
      const [maxPrice, minPrice] = (() => {
        let max: number | null = null;
        let min: number | null = null;
        if (option.__typename === 'PosOption') {
          option.variants.forEach((variant) => {
            if (typeof variant.price !== 'number') {
              return;
            }

            if (typeof max !== 'number' || variant.price > max) {
              max = variant.price;
            }

            if (typeof min !== 'number' || variant.price < min) {
              min = variant.price;
            }
          });
        }

        if (option.__typename === 'MenuOption') {
          option.variants.forEach(({ variablesByApp }) => {
            variablesByApp.forEach(({ price }) => {
              if (typeof price !== 'number') {
                return;
              }
              if (typeof max !== 'number' || price > max) {
                max = price;
              }

              if (typeof min !== 'number' || price < min) {
                min = price;
              }
            });
          });
        }
        return [max, min];
      })();

      const priceString =
        maxPrice === minPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;

      const products: Product[] = (() => {
        const _products: Product[] = allAppProducts.filter((product) => {
          return product.options.some((productOption) => {
            if (
              option.__typename === 'MenuOption'
                ? productOption.mmId === option.mmId
                : productOption.posId === option.posId
            ) {
              return true;
            }

            return false;
          });
        });

        return _products;
      })();

      const productsAlpha = [...products].sort((a, b) => a.name.localeCompare(b.name));

      return {
        item: {
          name: `${option.name} (${option.variants.length})`,
          imageUri: '/Images/NoImage.svg',
          price: priceString,
          key: option.__typename === 'MenuOption' ? option.mmId : option.posId,
          description: (() => {
            if (!productsAlpha.length) {
              return 'Not used in any products';
            }

            let response = productsAlpha[0].name;

            if (productsAlpha[1]) {
              response += `, ${productsAlpha[1].name}`;

              const lengthMinusTwo = productsAlpha.length - 2;

              if (lengthMinusTwo > 0) {
                response += ` + ${lengthMinusTwo} more`;
              }
            }

            return response;
          })()
        },
        enabled: !item.options.some(({ posId }) => option.posId === posId)
      };
    },
    [allAppProducts, item.options]
  );

  return (
    <section aria-label={buttonText} className={styles.Outer}>
      <ItemInlineSearch
        loading={isLoading}
        handleChange={(text) => debouncedSearch(text)}
        values={searchResults.map((option) => optionToItem(option))}
        handleSubmit={handleAddOption}
        handleLoadMore={searchMorePOSResults}
        setHasMore={setHasMore}
        hasMore={hasMore}
        buttonText={buttonText}
        initText={initText}
      />
    </section>
  );
};

export default AddOptionGroup;
